import { useEffect, useRef, useCallback } from 'react';

interface AutoLogoutOptions {
  /** Time in milliseconds after which user is automatically logged out due to inactivity */
  inactivityTimeout?: number;
  /** Time in milliseconds to show warning before logout */
  warningTime?: number;
  /** Whether to show a warning before logout */
  showWarning?: boolean;
  /** Custom warning message */
  warningMessage?: string;
  /** User object - if null, auto logout is disabled */
  user: any;
  /** Logout function to call when auto logout is triggered */
  logout: () => void;
}

export function useAutoLogout(options: AutoLogoutOptions) {
  const {
    inactivityTimeout = 30 * 60 * 1000, // 30 minutes default
    warningTime = 5 * 60 * 1000, // 5 minutes warning
    showWarning = true,
    warningMessage = 'You will be logged out due to inactivity in 5 minutes. Click anywhere to stay logged in.',
    user,
    logout
  } = options;
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const warningTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const lastActivityRef = useRef<number>(Date.now());
  const isLoggingOutRef = useRef<boolean>(false);

  // Function to reset the inactivity timer
  const resetInactivityTimer = useCallback(() => {
    if (!user || isLoggingOutRef.current) return;

    lastActivityRef.current = Date.now();
    
    // Clear existing timeouts
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    if (warningTimeoutRef.current) {
      clearTimeout(warningTimeoutRef.current);
    }

    // Set warning timeout
    if (showWarning) {
      warningTimeoutRef.current = setTimeout(() => {
        if (user && !isLoggingOutRef.current) {
          // Show warning
          const shouldContinue = window.confirm(warningMessage);
          if (shouldContinue) {
            resetInactivityTimer();
          } else {
            performLogout();
          }
        }
      }, inactivityTimeout - warningTime);
    }

    // Set logout timeout
    timeoutRef.current = setTimeout(() => {
      if (user && !isLoggingOutRef.current) {
        performLogout();
      }
    }, inactivityTimeout);
  }, [user, inactivityTimeout, warningTime, showWarning, warningMessage]);

  // Function to perform logout
  const performLogout = useCallback(() => {
    if (isLoggingOutRef.current) return;
    
    isLoggingOutRef.current = true;
    console.log('Auto logout triggered due to inactivity');
    logout();
  }, [logout]);

  // Function to handle page unload (browser close, tab close, navigation)
  const handlePageUnload = useCallback((event: BeforeUnloadEvent) => {
    if (user && !isLoggingOutRef.current) {
      // Clear any pending timeouts
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      if (warningTimeoutRef.current) {
        clearTimeout(warningTimeoutRef.current);
      }
      
      // Perform immediate logout
      performLogout();
    }
  }, [user, performLogout]);

  // Function to handle visibility change (tab switching, minimizing)
  const handleVisibilityChange = useCallback(() => {
    if (document.hidden && user && !isLoggingOutRef.current) {
      // When tab becomes hidden, set a shorter timeout for logout
      // This handles cases where user switches tabs or minimizes browser
      const hiddenTimeout = 5 * 60 * 1000; // 5 minutes when tab is hidden
      
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      
      timeoutRef.current = setTimeout(() => {
        if (user && !isLoggingOutRef.current) {
          performLogout();
        }
      }, hiddenTimeout);
    } else if (!document.hidden && user) {
      // When tab becomes visible again, reset the normal timer
      resetInactivityTimer();
    }
  }, [user, resetInactivityTimer, performLogout]);

  // Function to handle user activity
  const handleUserActivity = useCallback(() => {
    if (user && !isLoggingOutRef.current) {
      resetInactivityTimer();
    }
  }, [user, resetInactivityTimer]);

  // Set up event listeners
  useEffect(() => {
    if (!user) return;

    // Reset timer on user activity
    const activityEvents = [
      'mousedown',
      'mousemove',
      'keypress',
      'scroll',
      'touchstart',
      'click',
      'keydown'
    ];

    // Add activity event listeners
    activityEvents.forEach(event => {
      document.addEventListener(event, handleUserActivity, true);
    });

    // Add page unload listener
    window.addEventListener('beforeunload', handlePageUnload);

    // Add visibility change listener
    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Add page hide listener (for mobile browsers)
    window.addEventListener('pagehide', handlePageUnload);

    // Start the initial timer
    resetInactivityTimer();

    // Cleanup function
    return () => {
      activityEvents.forEach(event => {
        document.removeEventListener(event, handleUserActivity, true);
      });
      window.removeEventListener('beforeunload', handlePageUnload);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('pagehide', handlePageUnload);
      
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      if (warningTimeoutRef.current) {
        clearTimeout(warningTimeoutRef.current);
      }
    };
  }, [user, handleUserActivity, handlePageUnload, handleVisibilityChange, resetInactivityTimer]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      if (warningTimeoutRef.current) {
        clearTimeout(warningTimeoutRef.current);
      }
    };
  }, []);

  return {
    resetInactivityTimer,
    performLogout
  };
}
